<template>
  <div>
    <!-- Section 1: CSV Upload & Validation -->
    <section class="mb-6">
      <h3 class="mb-4">1. Upload Recipients</h3>
      <p class="text-body-2 mb-4">
        Your CSV must contain the following columns:
        <strong>recipient_name</strong>, <strong>phone_number</strong>, and
        <strong>account_number</strong>.
      </p>
      <SoFileInputField
        v-model="csvFile"
        class="mb-4"
        file-types=".csv"
        label="Upload CSV"
        placeholder="Select a CSV file"
        data-testid="sms-csv-upload"
      />
      <SoButton
        title="Validate"
        type="primary"
        large
        :disabled="csvFile.length === 0"
        :loading="validateMutator.isPending"
        data-testid="sms-validate-button"
        @click="handleValidate"
      />

      <div v-if="validateMutator.isPending" class="mt-4 d-flex align-center">
        <v-progress-circular
          indeterminate
          color="primary"
          size="24"
          class="mr-3"
        />
        <span class="text-body-2">Validating CSV...</span>
      </div>

      <div v-if="validationResult" class="mt-4">
        <SoAlert
          v-if="validationResult.invalidCount === 0"
          type="success"
          :text="`All ${validationResult.totalCount} recipients are valid.`"
          data-testid="sms-validation-success"
        />
        <SoAlert
          v-else-if="(validationResult.validCount ?? 0) > 0"
          type="warning"
          :text="validationWarningText"
          data-testid="sms-validation-warning"
        />
        <SoAlert
          v-else
          type="error"
          text="No valid recipients found. Please check your CSV and try again."
          data-testid="sms-validation-error"
        />

        <SoSimpleTable
          v-if="hasInvalidEntries"
          :headers="invalidEntriesTableHeaders"
          :items="invalidEntriesTableRows"
          class="mt-4"
          data-testid="sms-invalid-entries-table"
        />
      </div>

      <SoAlert
        v-if="validationError"
        type="error"
        :text="validationError"
        class="mt-4"
        data-testid="sms-validation-api-error"
      />
    </section>

    <!-- Section 2: Message Composer -->
    <section v-if="isValidated" class="mb-6">
      <h3 class="mb-4">2. Compose Message</h3>

      <div class="mb-2">
        <span class="text-body-2 mr-2">Insert variable:</span>
        <SoChip
          v-for="variable in SMS_VARIABLES"
          :key="variable"
          class="mr-2"
          size="small"
          :text="variable"
          data-testid="sms-variable-chip"
          @click="handleInsertVariable(variable)"
        />
      </div>

      <SoTextarea
        ref="messageTextarea"
        v-model="messageText"
        label="Message body"
        :rules="[rules.required]"
        :max-length="MAX_LENGTH"
        counter
        placeholder="Type your message here..."
        data-testid="sms-message-textarea"
      />

      <p class="text-body-2 mt-2">
        {{ charCount }} / {{ MAX_LENGTH }} characters &middot;
        {{ segmentCount }} SMS segment{{ segmentCount !== 1 ? "s" : "" }}
      </p>
    </section>

    <!-- Footer Actions -->
    <div v-if="isValidated" class="d-flex justify-space-between mt-6">
      <SoButton
        title="Cancel"
        large
        data-testid="sms-cancel-button"
        @click="resetForm"
      />
      <SoButton
        title="Send Messages"
        type="primary"
        large
        :disabled="!canSend"
        data-testid="sms-send-button"
        @click="showConfirmDialog = true"
      />
    </div>

    <SmsSenderConfirmDialog
      v-model="showConfirmDialog"
      :file="csvFile[0] || null"
      :message-body="messageText"
      :valid-count="validationResult?.validCount ?? 0"
      @sent="handleSent"
      @batch-sent="$emit('batch-sent')"
    />
  </div>
</template>

<script lang="ts">
  import { defineComponent } from "vue"
  import SoFileInputField from "@/components/formControls/SoFileInputField.vue"
  import SoTextarea from "@/components/formControls/SoTextarea.vue"
  import SoButton from "@/components/formControls/SoButton.vue"
  import SoAlert from "@/components/SoAlert.vue"
  import SoSimpleTable from "@/components/SoSimpleTable.vue"
  import SoChip from "@/components/SoChip.vue"
  import SmsSenderConfirmDialog from "./SmsSenderConfirmDialog.vue"
  import { SmsBatchValidationResponse } from "@/api/backofficeRest/generated/model"
  import { SmsBatchMutator } from "@/mutators/smsBatch/SmsBatchMutator"
  import {
    SMS_VARIABLES,
    MAX_LENGTH,
    getSegmentCount,
  } from "../composables/useMessageEditor"
  import type { SmsVariable } from "../composables/useMessageEditor"
  import { errorHandler, errorReporter } from "@/services/ErrorService"
  import rules from "@/validators/rules"

  export default defineComponent({
    components: {
      SoFileInputField,
      SoTextarea,
      SoButton,
      SoAlert,
      SoSimpleTable,
      SoChip,
      SmsSenderConfirmDialog,
    },
    emits: ["batch-sent"],
    data() {
      return {
        messageText: "",
        csvFile: [] as File[],
        validationResult: null as SmsBatchValidationResponse | null,
        validationError: null as string | null,
        showConfirmDialog: false,
        validateMutator: SmsBatchMutator.validate(),
        SMS_VARIABLES,
        MAX_LENGTH,
        rules,
      }
    },
    computed: {
      charCount(): number {
        return this.messageText.length
      },
      segmentCount(): number {
        return getSegmentCount(this.charCount)
      },
      isMessageValid(): boolean {
        return this.charCount > 0 && this.charCount <= MAX_LENGTH
      },
      isValidated(): boolean {
        return (
          this.validationResult !== null &&
          (this.validationResult.validCount ?? 0) > 0 &&
          !this.hasInvalidEntries
        )
      },
      canSend(): boolean {
        return this.isValidated && this.isMessageValid
      },
      hasInvalidEntries(): boolean {
        return (this.validationResult?.invalidCount ?? 0) > 0
      },
      validationWarningText(): string {
        const valid = this.validationResult?.validCount ?? 0
        const invalid = this.validationResult?.invalidCount ?? 0
        return `${valid} valid and ${invalid} invalid recipients found. Please fix the entries below in your CSV and re-upload.`
      },
      invalidEntriesTableHeaders(): string[] {
        return ["#", "Name", "Phone Number", "Reason"]
      },
      invalidEntriesTableRows(): (string | number)[][] {
        return (
          this.validationResult?.invalidEntries?.map((entry) => [
            entry?.rowNumber ?? "",
            entry?.recipientName ?? "",
            entry?.phoneNumber ?? "",
            entry?.reason ?? "",
          ]) ?? []
        )
      },
    },
    watch: {
      csvFile() {
        this.validationResult = null
        this.validationError = null
      },
    },
    methods: {
      async handleValidate() {
        if (this.csvFile.length === 0) return

        this.validationError = null
        this.validationResult = null

        try {
          this.validationResult = (await this.validateMutator.mutateAsync({
            file: this.csvFile[0],
          })) as SmsBatchValidationResponse
        } catch (error) {
          errorReporter.report(error)
          this.validationError = errorHandler.userMessage(
            error,
            "There was an error validating the CSV. Please try again.",
          )
        }
      },
      handleInsertVariable(variable: SmsVariable) {
        const textareaEl = (
          this.$refs.messageTextarea as InstanceType<typeof SoTextarea>
        )?.$el?.querySelector("textarea") as HTMLTextAreaElement | undefined
        const cursorPosition =
          textareaEl?.selectionStart ?? this.messageText.length
        this.insertVariable(variable, cursorPosition)
      },
      insertVariable(variable: SmsVariable, cursorPosition: number) {
        const before = this.messageText.slice(0, cursorPosition)
        const after = this.messageText.slice(cursorPosition)
        const newText = before + variable + after
        if (newText.length <= MAX_LENGTH) {
          this.messageText = newText
        }
      },
      handleSent() {
        this.resetForm()
      },
      resetForm() {
        this.csvFile = []
        this.validationResult = null
        this.validationError = null
        this.showConfirmDialog = false
        this.messageText = ""
      },
    },
  })
</script>
